import React, { useState, useEffect } from 'react';
import {
    ShieldAlert,
    ChevronLeft,
    Download,
    Plus,
    Package,
    Activity,
    Clock,
    Search,
    Filter,
    ArrowUpRight,
    Edit,
    History,
    AlertTriangle,
    Users,
    MapPin,
    RotateCcw,
    Check,
    Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import AddIssuanceRuleModal from './AddIssuanceRuleModal';

interface IssuanceLimitsProps {
    onBack: () => void;
}

const IssuanceLimits: React.FC<IssuanceLimitsProps> = ({ onBack }) => {
    const [settings, setSettings] = useState<any>(null);
    const [rules, setRules] = useState<any[]>([]);
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsData, rulesData, itemsData] = await Promise.all([
                api.get('/api/inventory/issuance-limits/settings'),
                api.get('/api/inventory/issuance-limits/rules'),
                api.get('/api/inventory/issuance-limits/items')
            ]);
            setSettings(settingsData);
            setRules(rulesData);
            setItems(itemsData);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch issuance limits data');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async (updates: any) => {
        try {
            const updated = await api.patch('/api/inventory/issuance-limits/settings', updates);
            setSettings(updated);
            toast.success('Settings updated');
        } catch (err) {
            toast.error('Failed to update settings');
        }
    };

    const toggleRuleActive = async (rule: any) => {
        try {
            const updatedRule = await api.post(`/api/inventory/issuance-limits/rules/${rule.id}`, {
                isActive: !rule.isActive
            });
            setRules(rules.map(r => r.id === rule.id ? updatedRule : r));
            toast.success(`Rule ${!rule.isActive ? 'activated' : 'deactivated'}`);
        } catch (err) {
            toast.error('Failed to toggle rule');
        }
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sku.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const specialRulesSummary = [
        { type: 'Value-Based', label: 'High-Value Items', desc: 'Items over $100 require manager approval', icon: <AlertTriangle size={20} />, color: 'orange', count: rules.filter(r => r.ruleType === 'Value-Based').length },
        { type: 'Time-Based', label: 'Time Restrictions', desc: 'PPE items limited to once per quarter', icon: <Clock size={20} />, color: 'blue', count: rules.filter(r => r.ruleType === 'Time-Based').length },
        { type: 'Role-Based', label: 'Role-Based Limits', desc: 'Different limits based on worker role', icon: <Users size={20} />, color: 'green', count: rules.filter(r => r.ruleType === 'Role-Based').length },
        { type: 'Location-Based', label: 'Location Restrictions', desc: 'Site-specific equipment limitations', icon: <MapPin size={20} />, color: 'purple', count: rules.filter(r => r.ruleType === 'Location-Based').length }
    ];

    return (
        <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-1.5 text-gray-400 hover:text-gray-900 transition-all p-0 h-auto hover:bg-transparent"
                    >
                        <ChevronLeft size={20} />
                        <span className="text-sm font-bold sm:inline hidden">Settings</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Issuance Limits</h1>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                            Limit Configuration
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <Button variant="outline" className="hidden sm:flex flex-initial rounded-2xl h-11 px-6 gap-2 border-gray-100 font-bold shadow-sm text-xs items-center hover:bg-gray-50 transition-all">
                        <Download size={16} />
                        Export Limits
                    </Button>
                    <Button
                        onClick={() => { setSelectedRule(null); setIsRuleModalOpen(true); }}
                        className="flex-1 sm:flex-initial items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl h-11 px-4 sm:px-6 font-bold shadow-lg shadow-slate-900/10 transition-all text-xs sm:text-sm"
                    >
                        <Plus size={18} />
                        <span className="whitespace-nowrap">Add Rule</span>
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Global Limit Settings */}
                <div className="bg-white p-5 sm:p-8 rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm space-y-5 sm:space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                            <ShieldAlert size={20} />
                        </div>
                        <h3 className="text-base sm:text-lg font-black text-gray-900 tracking-tight">Global Limit Settings</h3>
                    </div>

                    <div className="space-y-5 sm:space-y-6">
                        <div className="space-y-1.5">
                            <label className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Default Uniform Limit</label>
                            <Input
                                type="number"
                                value={settings?.defaultUniformLimit || 0}
                                onChange={(e) => handleUpdateSettings({ defaultUniformLimit: parseInt(e.target.value) })}
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-lg"
                            />
                            <p className="text-[10px] text-gray-400 font-medium ml-1">Applied to new uniform items</p>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Default PPE Limit</label>
                            <Input
                                type="number"
                                value={settings?.defaultPpeLimit || 0}
                                onChange={(e) => handleUpdateSettings({ defaultPpeLimit: parseInt(e.target.value) })}
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-lg"
                            />
                            <p className="text-[10px] text-gray-400 font-medium ml-1">Applied to new PPE items</p>
                        </div>

                        <div className="flex items-center justify-between p-3 sm:p-4 bg-orange-50/30 border border-orange-100/50 rounded-2xl gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-orange-500 shadow-sm border border-orange-100/50 shrink-0">
                                    <Check size={16} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs sm:text-sm font-bold text-gray-800 leading-tight">Require approval for over-limit</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleUpdateSettings({ requireApprovalForOverLimit: !settings?.requireApprovalForOverLimit })}
                                className={cn(
                                    "w-10 h-5 rounded-full relative transition-all duration-300 shrink-0",
                                    settings?.requireApprovalForOverLimit ? "bg-orange-500" : "bg-gray-200"
                                )}
                            >
                                <div className={cn(
                                    "absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300",
                                    settings?.requireApprovalForOverLimit ? "left-6" : "left-1"
                                )} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Time-Based Limits */}
                <div className="bg-white p-5 sm:p-8 rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm space-y-5 sm:space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-400">
                            <Clock size={20} />
                        </div>
                        <h3 className="text-base sm:text-lg font-black text-gray-900 tracking-tight">Time-Based Limits</h3>
                    </div>

                    <div className="space-y-5 sm:space-y-6">
                        <div className="space-y-1.5">
                            <label className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Max Issuances / Worker / Month</label>
                            <Input
                                type="number"
                                value={settings?.maxIssuancesPerWorkerPerMonth || 0}
                                onChange={(e) => handleUpdateSettings({ maxIssuancesPerWorkerPerMonth: parseInt(e.target.value) })}
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-lg"
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Cooldown Period (days)</label>
                            <Input
                                type="number"
                                value={settings?.cooldownPeriodDays || 0}
                                onChange={(e) => handleUpdateSettings({ cooldownPeriodDays: parseInt(e.target.value) })}
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-bold text-lg"
                            />
                            <p className="text-[10px] text-gray-400 font-medium ml-1">Time before same item can be reissued</p>
                        </div>

                        <div className="flex items-center justify-between p-3 sm:p-4 bg-blue-50/30 border border-blue-100/50 rounded-2xl gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <RotateCcw size={18} className="text-blue-500 shrink-0" />
                                <div className="min-w-0">
                                    <p className="text-xs sm:text-sm font-bold text-gray-800 leading-tight">Track returns for reissuance</p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleUpdateSettings({ trackReturnsForReissuance: !settings?.trackReturnsForReissuance })}
                                className={cn(
                                    "w-10 h-5 rounded-full relative transition-all duration-300 shrink-0",
                                    settings?.trackReturnsForReissuance ? "bg-blue-500" : "bg-gray-200"
                                )}
                            >
                                <div className={cn(
                                    "absolute top-1 w-3 h-3 bg-white rounded-full transition-all duration-300",
                                    settings?.trackReturnsForReissuance ? "left-6" : "left-1"
                                )} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="bg-white p-4 sm:p-5 rounded-[32px] border border-gray-100 shadow-sm space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-4">
                <div className="flex-1 relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={20} />
                    <Input
                        placeholder="Search items..."
                        className="pl-12 rounded-[20px] border-gray-100 bg-gray-50/50 h-12 transition-all focus:bg-white focus:ring-4 focus:ring-blue-500/5 font-medium text-sm border-none shadow-inner"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 sm:flex gap-3">
                    <Select defaultValue="all">
                        <SelectTrigger className="w-full sm:w-40 rounded-[18px] border-gray-100 h-11 font-bold text-xs bg-gray-50/50 hover:bg-white transition-all">
                            <div className="flex items-center gap-2">
                                <Filter size={14} className="text-gray-400" />
                                <SelectValue placeholder="All Types" />
                            </div>
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="ppe">PPE Only</SelectItem>
                            <SelectItem value="uniform">Uniform Only</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select defaultValue="all">
                        <SelectTrigger className="w-full sm:w-40 rounded-[18px] border-gray-100 h-11 font-bold text-xs bg-gray-50/50 hover:bg-white transition-all">
                            <div className="flex items-center gap-2">
                                <ShieldAlert size={14} className="text-gray-400" />
                                <SelectValue placeholder="All Limits" />
                            </div>
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl border-gray-100">
                            <SelectItem value="all">All Limits</SelectItem>
                            <SelectItem value="restricted">Restricted</SelectItem>
                            <SelectItem value="approval">Requires Approval</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Loading State */}
            {loading && filteredItems.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                    <Loader2 size={28} className="animate-spin text-blue-500" />
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Loading Items...</p>
                </div>
            )}

            {/* Empty State */}
            {!loading && filteredItems.length === 0 && (
                <div className="bg-white rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm p-12 sm:p-20 flex flex-col items-center justify-center text-center">
                    <div className="w-20 h-20 bg-gray-50 rounded-[28px] flex items-center justify-center mb-6 shadow-inner">
                        <Package size={36} className="text-gray-300" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-2">No Items Found</h3>
                    <p className="text-xs sm:text-sm text-gray-400 max-w-xs font-bold leading-relaxed">Items with issuance limits will appear here.</p>
                </div>
            )}

            {/* Mobile Card View */}
            {filteredItems.length > 0 && (
                <div className="md:hidden space-y-3">
                    <div className="flex items-center gap-3 px-1">
                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                            <ShieldAlert className="text-gray-400" size={16} />
                        </div>
                        <h2 className="text-sm font-black text-gray-900 tracking-tight">Item Limits</h2>
                        <Badge variant="outline" className="rounded-full text-[9px] font-bold px-2 py-0.5 border-gray-200">{filteredItems.length}</Badge>
                    </div>
                    {filteredItems.map((item) => (
                        <div key={item.id} className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-4 space-y-3">
                                {/* Item Header */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={cn(
                                            "w-11 h-11 rounded-[14px] flex items-center justify-center shrink-0",
                                            item.category?.name === 'PPE' ? 'bg-orange-50 text-orange-500' : 'bg-blue-50 text-blue-500'
                                        )}>
                                            <Package size={20} />
                                        </div>
                                        <div className="min-w-0">
                                            <h3 className="text-sm font-black text-gray-900 truncate">{item.name}</h3>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">SKU: {item.sku}</p>
                                        </div>
                                    </div>
                                    <Badge className={cn(
                                        "rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-wider shrink-0 border-0",
                                        item.category?.name === 'PPE' ? "bg-orange-50 text-orange-600" : "bg-blue-50 text-blue-600"
                                    )}>
                                        {item.category?.name || 'GEN'}
                                    </Badge>
                                </div>

                                {/* Stats Row */}
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-gray-50/50 rounded-[14px] p-3 text-center">
                                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Limit</p>
                                        <p className="text-base font-black text-gray-900 mt-0.5">{item.issuanceLimit || 0}</p>
                                    </div>
                                    <div className={cn(
                                        "rounded-[14px] p-3 text-center",
                                        item.currentStock === 0 ? "bg-red-50/50" : item.currentStock < 20 ? "bg-orange-50/50" : "bg-green-50/50"
                                    )}>
                                        <p className={cn(
                                            "text-[8px] font-black uppercase tracking-widest",
                                            item.currentStock === 0 ? "text-red-400" : item.currentStock < 20 ? "text-orange-400" : "text-green-400"
                                        )}>Stock</p>
                                        <p className={cn(
                                            "text-base font-black mt-0.5",
                                            item.currentStock === 0 ? "text-red-600" : item.currentStock < 20 ? "text-orange-600" : "text-gray-900"
                                        )}>{item.currentStock}</p>
                                    </div>
                                    <div className="bg-purple-50/50 rounded-[14px] p-3 text-center">
                                        <p className="text-[8px] font-black text-purple-400 uppercase tracking-widest">Usage</p>
                                        <p className="text-base font-black text-gray-900 mt-0.5">12</p>
                                    </div>
                                </div>

                                {/* Approval & Status */}
                                <div className="flex items-center gap-3 px-1">
                                    {item.requiresApproval && (
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-purple-500">
                                            <div className="w-4 h-4 rounded-md bg-purple-500 flex items-center justify-center">
                                                <Check size={10} className="text-white" />
                                            </div>
                                            Approval Required
                                        </div>
                                    )}
                                    {item.currentStock === 0 && (
                                        <span className="text-[9px] font-black text-red-500 uppercase tracking-widest animate-pulse">Out of Stock</span>
                                    )}
                                    {item.currentStock > 0 && item.currentStock < 20 && (
                                        <span className="text-[9px] font-black text-orange-500 uppercase tracking-widest animate-pulse">Low Stock</span>
                                    )}
                                </div>
                            </div>

                            {/* Mobile Action Bar */}
                            <div className="border-t border-gray-50 px-3 py-2 flex items-center justify-between bg-gray-50/30">
                                <Button variant="ghost" size="sm" className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-blue-600 hover:bg-blue-50 gap-1.5">
                                    <Edit size={12} />
                                    Edit Limit
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 px-3 rounded-lg text-[10px] font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 gap-1.5">
                                    <History size={12} />
                                    History
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Desktop Item Table */}
            {filteredItems.length > 0 && (
                <div className="hidden md:block bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
                    <div className="p-8 border-b border-gray-50 flex items-center gap-3">
                        <ShieldAlert className="text-gray-400" size={24} />
                        <h2 className="text-xl font-bold text-gray-900">Item Limit Configuration</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50/50">
                                    <th className="text-left py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Item Details</th>
                                    <th className="text-left py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Category</th>
                                    <th className="text-left py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Current Limit</th>
                                    <th className="text-left py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Stock Level</th>
                                    <th className="text-left py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Recent Usage</th>
                                    <th className="text-center py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Approval Required</th>
                                    <th className="text-right py-5 px-8 text-[11px] font-black text-gray-400 uppercase tracking-widest">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredItems.map((item) => (
                                    <tr key={item.id} className="group hover:bg-gray-50/50 transition-colors">
                                        <td className="py-5 px-8">
                                            <div className="flex items-center gap-4">
                                                <div className={cn(
                                                    "w-10 h-10 rounded-xl flex items-center justify-center transition-colors shadow-sm",
                                                    item.category?.name === 'PPE' ? 'bg-orange-50 text-orange-500' : 'bg-blue-50 text-blue-500'
                                                )}>
                                                    <Package size={20} />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 leading-none mb-1">{item.name}</p>
                                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">SKU: {item.sku}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-5 px-8">
                                            <Badge className={cn(
                                                "rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                                                item.category?.name === 'PPE' ? "bg-orange-50 text-orange-600 border-orange-100" : "bg-blue-50 text-blue-600 border-blue-100"
                                            )}>
                                                {item.category?.name || 'GEN'}
                                            </Badge>
                                        </td>
                                        <td className="py-5 px-8">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-gray-900 min-w-[1.5rem] text-center">{item.issuanceLimit || 0}</span>
                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">per issuance</span>
                                            </div>
                                        </td>
                                        <td className="py-5 px-8">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-gray-900">{item.currentStock}</span>
                                                {item.currentStock === 0 ? (
                                                    <span className="text-[9px] font-black text-red-500 uppercase tracking-widest mt-0.5 animate-pulse">Out of Stock</span>
                                                ) : item.currentStock < 20 && (
                                                    <span className="text-[9px] font-black text-red-500 uppercase tracking-widest mt-0.5 animate-pulse">Low Stock</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-5 px-8">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-gray-900">12</span>
                                                <span className="text-[10px] text-gray-400 font-medium">Last 30 days</span>
                                            </div>
                                        </td>
                                        <td className="py-5 px-8 text-center">
                                            <div className="flex justify-center">
                                                <div className={cn(
                                                    "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer",
                                                    item.requiresApproval ? "bg-purple-500 border-purple-500" : "bg-white border-gray-100"
                                                )}>
                                                    {item.requiresApproval && <Check size={14} className="text-white" />}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-5 px-8 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-white hover:shadow-sm">
                                                    <Edit size={14} className="text-gray-400" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-white hover:shadow-sm">
                                                    <History size={14} className="text-gray-400" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Special Rules */}
            <div className="relative bg-white rounded-[24px] sm:rounded-[32px] border border-gray-100 shadow-sm p-5 sm:p-8 space-y-6 sm:space-y-8 overflow-hidden">
                {/* Coming Soon Overlay */}
                <div className="absolute inset-0 z-10 bg-white/90 flex items-center justify-center">
                    <span className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">Coming soon...</span>
                </div>

                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                        <ShieldAlert className="text-gray-400" size={20} />
                    </div>
                    <h2 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight">Special Issuance Rules</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {specialRulesSummary.map((card, i) => (
                        <div key={i} className="bg-gray-50/30 rounded-[20px] sm:rounded-[28px] border border-gray-100/50 p-4 sm:p-6 flex items-start gap-3 sm:gap-4 group hover:bg-white hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300">
                            <div className={cn(
                                "w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-all group-hover:scale-110 shrink-0",
                                card.color === 'orange' ? 'bg-orange-50 text-orange-500' :
                                    card.color === 'blue' ? 'bg-blue-50 text-blue-500' :
                                        card.color === 'green' ? 'bg-green-50 text-green-500' : 'bg-purple-50 text-purple-500'
                            )}>
                                {card.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm sm:text-base font-black text-gray-900 mb-1 tracking-tight">{card.label}</h4>
                                <p className="text-[11px] sm:text-sm text-gray-500 mb-3 sm:mb-4 leading-relaxed">{card.desc}</p>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <Badge variant="outline" className="rounded-full sm:rounded-lg bg-white border-gray-100 shadow-sm font-bold text-[9px] sm:text-[10px] px-2 py-0.5 w-fit">
                                        Rules: {card.count}
                                    </Badge>
                                    <Button variant="ghost" size="sm" className="h-7 sm:h-8 rounded-lg text-[9px] sm:text-[10px] font-black uppercase tracking-wider group-hover:text-blue-600 px-2 w-fit">
                                        Manage <ArrowUpRight size={12} className="ml-1" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <AddIssuanceRuleModal
                isOpen={isRuleModalOpen}
                onClose={() => setIsRuleModalOpen(false)}
                onRefresh={fetchData}
                rule={selectedRule}
            />
        </div>
    );
};

export default IssuanceLimits;
